import { getAdQueryOptions } from '@/features/meta-ads/api/get-ad/get-ad.hook';
import type { CampaignPackageReview } from '@borradh-workspace/contracts/claire';
import { useQueryClient } from '@tanstack/react-query';
import type { UIMessage } from 'ai';
import { useEffect } from 'react';

import {
  asToolPart,
  getToolName,
  hasToolResultFailure,
} from '../../lib/tool-parts';

import { AdPreviewCard } from './ad-preview-card';

// Older saved reviews embedded the form contents in the campaign's fields.
// Keep those conversations concise too, without rewriting their history.
const LEGACY_FORM_FIELDS = new Set([
  'Lead form',
  'Form questions',
  'Privacy policy',
  'Thank-you heading',
  'Thank-you message',
  'Thank-you destination',
]);

/** Refresh only saved ads named by an actual package execution result. */
export function CampaignPackageAdStatusRefresh({
  messages,
}: { messages: UIMessage[] }) {
  return messages.flatMap((message) =>
    message.role !== 'assistant'
      ? []
      : message.parts.flatMap((part) => {
          const tool = asToolPart(part);
          if (
            !tool ||
            getToolName(tool) !== 'meta_ads_launchCampaignPackage' ||
            tool.state !== 'output-available' ||
            hasToolResultFailure(tool) ||
            !['completed', 'partial', 'unverified'].includes(
              String(tool.output?.status)
            ) ||
            tool.output?.campaignId !== tool.input?.campaignId ||
            !Array.isArray(tool.input?.adIds) ||
            !Array.isArray(tool.output?.ads)
          )
            return [];
          const requestedIds = new Set(tool.input.adIds);
          const adIds = tool.output.ads.flatMap((ad) =>
            ad &&
            typeof ad === 'object' &&
            'adId' in ad &&
            typeof ad.adId === 'string' &&
            requestedIds.has(ad.adId)
              ? [ad.adId]
              : []
          );
          return adIds.length ? (
            <RefreshSavedAds
              key={`${message.id}:${tool.toolCallId}`}
              adIdsKey={JSON.stringify([...new Set(adIds)].sort())}
            />
          ) : (
            []
          );
        })
  );
}

function RefreshSavedAds({ adIdsKey }: { adIdsKey: string }) {
  const queryClient = useQueryClient();
  useEffect(() => {
    const adIds: string[] = JSON.parse(adIdsKey);
    for (const adId of adIds) {
      void queryClient.invalidateQueries({
        queryKey: getAdQueryOptions(adId).queryKey,
        exact: true,
      });
    }
  }, [adIdsKey, queryClient]);
  return null;
}

function readableSchedule(value: string): string {
  return value.replace(
    /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})/g,
    (timestamp) => {
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) return timestamp;
      return new Intl.DateTimeFormat(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      }).format(date);
    }
  );
}

/** Saved ad snapshots stay in chat; only the package decision moves to the composer. */
export function CampaignPackageAdPreviews({
  review,
  onSendMessage,
}: { review: CampaignPackageReview; onSendMessage?: (text: string) => void }) {
  const budget =
    review.campaign.fields.find((field) => field.label === 'Daily budget') ??
    review.campaign.fields.find((field) => field.label === 'Budget');
  const area = review.campaign.fields.find(
    (field) => field.label === 'Area' || field.label === 'Location'
  );
  const fields = review.campaign.fields
    .filter(
      (field) =>
        field !== budget &&
        field !== area &&
        field.label !== 'Account' &&
        !LEGACY_FORM_FIELDS.has(field.label)
    )
    .map((field) =>
      field.label === 'Schedule'
        ? { ...field, value: readableSchedule(field.value) }
        : field
    );
  const details = [
    ...(fields.length ? [{ title: review.campaign.title, fields }] : []),
    // The offer's saved terms, so the owner approves what goes live and not
    // just the offer's name. Older saved reviews held long free text here
    // instead, which stays hidden; only the structured terms carry this label.
    ...(review.offer?.fields.some((field) => field.label === 'Promotion terms')
      ? [
          {
            title: `Promotion: ${review.offer.title}`,
            fields: review.offer.fields,
          },
        ]
      : []),
  ];

  return (
    <div className="mb-3 flex flex-col items-start gap-6">
      {review.ads.map((ad) => (
        <CampaignPackageAdPreview
          key={ad.adId}
          ad={ad}
          review={review}
          onSendMessage={onSendMessage}
          budgetDisplay={budget?.value}
          targetingDisplay={area?.value}
          details={details}
        />
      ))}
    </div>
  );
}

function CampaignPackageAdPreview({
  ad,
  review,
  onSendMessage,
  budgetDisplay,
  targetingDisplay,
  details,
}: {
  ad: CampaignPackageReview['ads'][number];
  onSendMessage?: (text: string) => void;
  review: CampaignPackageReview;
  budgetDisplay?: string;
  targetingDisplay?: string;
  details: { title: string; fields: { label: string; value: string }[] }[];
}) {
  const promotion = review.offer
    ? { id: review.offer.id, name: review.offer.title }
    : undefined;
  const leadForm = ad.leadForm;

  return (
    <AdPreviewCard
      variant="draft"
      onSendMessage={onSendMessage}
      adName={ad.title}
      adId={ad.adId}
      headline={ad.headline}
      primaryText={ad.primaryText}
      description={ad.description}
      callToAction={ad.callToAction}
      campaignName={review.campaign.title}
      budgetDisplay={budgetDisplay}
      targetingDisplay={targetingDisplay}
      destinationUrl={ad.destinationUrl}
      assetImageUrl={ad.media.type === 'image' ? ad.media.url : undefined}
      assetVideoUrl={ad.media.type === 'video' ? ad.media.url : undefined}
      assetThumbnailUrl={
        ad.media.type === 'video' ? ad.media.posterUrl : undefined
      }
      promotion={promotion}
      leadForm={leadForm}
      details={details}
    />
  );
}
